import { Injectable, inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { validateCreditCard, validateExpirationDate } from '@sbd-doug-frontend/shared/lib/public-validators';
import {
  ShoppingCartPaymentProvider,
  ShoppingCartPaymentData,
  PaymentProcessResult,
  PaymentFormData,
  PaymentFormFieldConfig
} from '@sbd-doug-frontend/shared/lib/shopping-cart/payment/shopping-cart-payment.types';
import { PaymentService } from '../modules/payment/payment.service';
import { OpenQuoteRequestsWidgetService } from '../modules/dashboard/open-quote-requests-widget/open-quote-requests-widget.service';

@Injectable({
  providedIn: 'root'
})
export class AccountPortalPaymentProviderService implements ShoppingCartPaymentProvider {
  private _formBuilder = inject(FormBuilder);
  private _paymentService = inject(PaymentService);
  private _openQuoteRequestsWidgetService = inject(OpenQuoteRequestsWidgetService);

  createPaymentForm(): FormGroup {
    return this._formBuilder.group({
      payer_name: ['', [Validators.required]],
      credit_card_cvv: ['', [Validators.required, Validators.maxLength(3)]],
      expiration_date: ['', [Validators.required, validateExpirationDate]],
      credit_card_number: ['', [Validators.required, validateCreditCard]],
      useDefaultAddress: [true, [Validators.required]],
      address_street_1: [''],
      address_street_2: [''],
      address_city: [''],
      address_state_abbreviation: [''],
      address_zip: [''],
      selectedCardType: [''],
      paymentMethod: ['CREDIT_CARD'],
      quoteRequestIDs: [[]]
    });
  }

  processPayment(formData: PaymentFormData, paymentData: ShoppingCartPaymentData): Observable<PaymentProcessResult> {

    // Extract quote request IDs from cart items
    const quoteRequestIDs = paymentData.items
      .map(item => item.data?.quote_request_id)
      .filter(id => id !== undefined && id !== null);

    const paymentRequest = {
      payer_name: (formData as any).payer_name,
      credit_card_cvv: formData.credit_card_cvv,
      expiration_date: formData.expiration_date,
      credit_card_number: formData.credit_card_number,
      useDefaultAddress: (formData as any).useDefaultAddress,
      address_street_1: (formData as any).address_street_1,
      address_street_2: (formData as any).address_street_2,
      address_city: (formData as any).address_city,
      address_state_abbreviation: (formData as any).address_state_abbreviation,
      address_zip: (formData as any).address_zip,
      selectedCardType: (formData as any).selectedCardType,
      paymentMethod: 'CREDIT_CARD',
      quoteRequestIDs: quoteRequestIDs
    };





    return this._paymentService.purchaseQuoteRequest(paymentRequest).pipe(
      map(response => {
        if (response && response.success) {
          const result = {
            success: true,
            transactionId: response.data?.transaction?.confirmation || 'unknown',
            data: response.data
          };
          return result;
        } else {
          return {
            success: false,
            error: response?.message || 'Payment processing failed'
          };
        }
      }),
      tap(result => {
        // Reload quote requests after successful payment
        if (result.success) {
          this._openQuoteRequestsWidgetService.getOpenQuoteRequests().subscribe();
        }
      }),
      catchError(error => {
        // Handle different types of errors
        let errorMessage = 'Payment processing failed';

        if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.status) {
          errorMessage = `HTTP ${error.status}: ${error.statusText || 'Unknown error'}`;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }

        return of({
          success: false,
          error: errorMessage
        });
      })
    );
  }

  validateCardNumber(cardNumber: string): { cardType: string; isValid: boolean } {
    if (!cardNumber) {
      return { cardType: '', isValid: false };
    }

    // Remove spaces and dashes
    const cleanCardNumber = cardNumber.replace(/[\s-]/g, '');

    // Regex patterns for card types
    const visaRegex = /^4[0-9]{12}(?:[0-9]{3})?$/;
    const mastercardRegex = /^5[1-5][0-9]{14}$/;
    const amexRegex = /^3[47][0-9]{13}$/;

    if (visaRegex.test(cleanCardNumber)) {
      return { cardType: 'visa', isValid: true };
    } else if (mastercardRegex.test(cleanCardNumber)) {
      return { cardType: 'mastercard', isValid: true };
    } else if (amexRegex.test(cleanCardNumber)) {
      return { cardType: 'amex', isValid: true };
    } else {
      return { cardType: '', isValid: cleanCardNumber.length >= 13 && cleanCardNumber.length <= 19 };
    }
  }

  getFormFieldConfig(): PaymentFormFieldConfig {
    return {
      requiresBillingAddress: false, // We handle this with the address form component
      requiresCardholderName: false, // We use payer_name instead
      supportedCardTypes: ['visa', 'mastercard', 'amex'],
      additionalFields: []
    };
  }
}
